import { WORK_PERMIT_STATUS } from '../consts'
import {
  WP_HISTORY_DISPLAY_STATUSES,
  WP_REVIEWER_HISTORY_STATUSES,
} from './contracts'
import { resolveCommentContent } from './commentContent'
import { getWorkPermitHistoryDisplayDecision } from './displayDecision'
import { canExternalViewerSeeHistorySection } from './visibilityPolicy'
import { resolveReviewerHistoryExternalDisclosedCheckbox } from './reviewerExternalDisclosureCheckbox'

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Read history row created time from top-level or Sequelize-style dataValues.
 * @param {Object} row
 * @returns {*}
 */
function getHistoryRowCreatedAt (row) {
  if (!row) return null
  if (row.createdAt != null && row.createdAt !== '') {
    return row.createdAt
  }
  return row.dataValues && row.dataValues.createdAt
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Read history row status from top-level or Sequelize-style dataValues.
 * @param {Object} row
 * @returns {*}
 */
function getHistoryRowStatus (row) {
  if (row == null || typeof row !== 'object') {
    return undefined
  }
  if (row.status != null && row.status !== '') {
    return row.status
  }
  if (row.dataValues && row.dataValues.status != null && row.dataValues.status !== '') {
    return row.dataValues.status
  }
  return undefined
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Normalize known Work Permit history statuses while preserving unknown values.
 * @param {*} raw
 * @returns {*}
 */
function canonicalWorkPermitHistoryStatus (raw) {
  if (raw == null) return null
  const values = Object.values(WORK_PERMIT_STATUS)
  for (let i = 0; i < values.length; i += 1) {
    if (raw === values[i]) {
      return values[i]
    }
  }
  if (typeof raw === 'string') {
    const lower = raw.trim().toLowerCase()
    for (let j = 0; j < values.length; j += 1) {
      if (values[j] && String(values[j]).toLowerCase() === lower) {
        return values[j]
      }
    }
  }
  return raw
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Sort history rows by their created time in ascending order.
 * @param {Array} histories
 * @returns {Array}
 */
function sortHistoriesByCreatedAt (histories) {
  return (histories || []).slice().sort((a, b) => {
    const at = getHistoryRowCreatedAt(a)
    const bt = getHistoryRowCreatedAt(b)
    return new Date(at) - new Date(bt)
  })
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Select the original latest Work Permit comment display cycle from the last WAPPR row.
 * @param {Array} histories
 * @returns {Array}
 */
export function getLatestWorkPermitHistoryDisplayCycle (histories) {
  const sorted = sortHistoriesByCreatedAt(histories)
  if (!sorted.length) return []
  let wapprIndex = -1
  sorted.forEach((h, i) => {
    if (canonicalWorkPermitHistoryStatus(getHistoryRowStatus(h)) === WORK_PERMIT_STATUS.WAPPR) {
      wapprIndex = i
    }
  })
  if (wapprIndex < 0) {
    return sorted
  }
  return sorted.slice(wapprIndex)
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Group history rows by canonical Work Permit status.
 * @param {Array} histories
 * @returns {Object}
 */
function groupHistoriesByCanonicalStatus (histories) {
  return histories.reduce((result, h) => {
    const raw = getHistoryRowStatus(h)
    const canonical = canonicalWorkPermitHistoryStatus(raw)
    const key = canonical != null ? canonical : raw
    if (key == null) return result
    if (!result[key]) result[key] = []
    result[key].push(h)
    return result
  }, {})
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Pick the earliest row as the representative for duplicate same-status histories.
 * @param {Array} rows
 * @returns {Object|null}
 */
function pickRepresentativeHistory (rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null
  return rows.slice().sort((a, b) => {
    const at = getHistoryRowCreatedAt(a)
    const bt = getHistoryRowCreatedAt(b)
    return new Date(at) - new Date(bt)
  })[0] || null
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Resolve the byline translation key for engineer-visible history statuses.
 * @param {string} status
 * @returns {string}
 */
function resolveEngineerHistoryBylineKey (status) {
  const bylineKeyMap = {
    [WORK_PERMIT_STATUS.APPR]: 'workPermit.historyCommentApprovedByOn',
    [WORK_PERMIT_STATUS.REJECTED]: 'workPermit.rejectedBy',
    [WORK_PERMIT_STATUS.ACKNOWLEDGED]: 'workPermit.historyCommentAcknowledgedByOn',
  }
  if (bylineKeyMap[status]) {
    return bylineKeyMap[status]
  }
  if (WP_REVIEWER_HISTORY_STATUSES.includes(status)) {
    return 'reviewedBy'
  }
  /* istanbul ignore next */
  return 'workPermit.historyCommentByOn'
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Resolve the byline translation key for a display row.
 * External viewers use a neutral "by/on" phrase; engineers keep status-specific wording.
 * @param {string} status
 * @param {boolean} isEngineer
 * @returns {string}
 */
function resolveBylineKey (status, isEngineer) {
  if (!isEngineer) {
    return 'workPermit.historyCommentByOn'
  }
  return resolveEngineerHistoryBylineKey(status)
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Resolve the heading title for engineer-visible history rows.
 * @param {string} status
 * @param {Function} translate
 * @returns {string}
 */
function resolveEngineerHistoryTitle (status, translate) {
  if (status === WORK_PERMIT_STATUS.APPR) {
    return translate('workPermit.commentsfromApprover')
  }
  return translate('workPermit.commentsfromReviewer')
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Resolve the heading title for one history display row.
 * @param {string} status
 * @param {boolean} isEngineer
 * @param {Function} translate
 * @returns {string|undefined}
 */
function resolveTitle (status, isEngineer, translate) {
  if (isEngineer) return resolveEngineerHistoryTitle(status, translate)
  if ([
    WORK_PERMIT_STATUS.ACKNOWLEDGED,
    WORK_PERMIT_STATUS.REVIEWED,
    WORK_PERMIT_STATUS.REVIEWED2,
    WORK_PERMIT_STATUS.REJECTED,
  ].includes(status)) {
    return translate('workPermit.commentsfromReviewer')
  }
  if (status === WORK_PERMIT_STATUS.APPR) {
    return translate('workPermit.commentsfromApprover')
  }
  /* istanbul ignore next */
  return undefined
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Default translation fallback for unit tests and mock lab usage.
 * @param {string} key
 * @param {Array} args
 * @returns {string}
 */
function defaultTranslate (key, args) {
  return args && args.length ? `${key} ${args.join(' ')}` : key
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Default date formatter fallback for unit tests and mock lab usage.
 * @param {*} value
 * @returns {*}
 */
function defaultFormatDate (value) {
  return value
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Default history actor label fallback for unit tests and mock lab usage.
 * @returns {string}
 */
function defaultHistoryWhoLabel () {
  return '--'
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Build display-ready history rows from raw histories.
 * This centralizes the current WPCollapseContent pipeline without changing output shape.
 * @param {Object} params
 * @param {Array} params.histories
 * @param {boolean} params.isEngineer
 * @param {string} params.permitStatus
 * @param {string} [params.emptyValue]
 * @param {Function} [params.translate]
 * @param {Function} [params.formatDate]
 * @param {Function} [params.historyWhoLabel]
 * @returns {Array}
 */
export function buildWorkPermitHistoryDisplayRows ({
  histories = [],
  isEngineer,
  permitStatus,
  emptyValue = '--',
  translate = defaultTranslate,
  formatDate = defaultFormatDate,
  historyWhoLabel = defaultHistoryWhoLabel,
} = {}) {
  const latestReviewCycleHistories = getLatestWorkPermitHistoryDisplayCycle(histories)
  if (!latestReviewCycleHistories.length) return []

  const grouped = groupHistoriesByCanonicalStatus(latestReviewCycleHistories)
  const comments = {}
  WP_HISTORY_DISPLAY_STATUSES.forEach(status => {
    comments[status] = {}
    if (!grouped[status]) return
    const ref = pickRepresentativeHistory(grouped[status])
    if (!ref) return
    const detail = ref.detail || {}
    const { content, externalContent } = resolveCommentContent(status, detail, emptyValue)
    const refCreated = getHistoryRowCreatedAt(ref)
    const dateStr = formatDate(refCreated, 'DD MMM YY HH:mm')
    const name = historyWhoLabel(ref)
    const byKey = resolveBylineKey(status, isEngineer)
    comments[status] = {
      content,
      externalContent,
      leaveExternalComments: !!detail.leaveExternalComments,
      persistedExternalDisclosedToContractor: resolveReviewerHistoryExternalDisclosedCheckbox(detail),
      by: translate(byKey, [name, dateStr]),
      title: resolveTitle(status, isEngineer, translate),
    }
  })

  const pushBlock = (blocks, status) => {
    if (!comments[status] || !comments[status].by) return
    const item = {
      ...comments[status],
      status,
      index: blocks.length + 1,
    }
    item.decision = getWorkPermitHistoryDisplayDecision({ item, isEngineer, emptyValue })
    blocks.push(item)
  }

  const hasRejected = !!latestReviewCycleHistories.find(h => getHistoryRowStatus(h) === WORK_PERMIT_STATUS.REJECTED)
  const hasApproved = !!latestReviewCycleHistories.find(h => getHistoryRowStatus(h) === WORK_PERMIT_STATUS.APPR)
  let orderedBlocks = []
  if (!isEngineer) {
    if (hasRejected) {
      pushBlock(orderedBlocks, WORK_PERMIT_STATUS.REJECTED)
    } else if (!hasApproved) {
      orderedBlocks = []
    } else {
      [
        ...WP_REVIEWER_HISTORY_STATUSES,
        WORK_PERMIT_STATUS.APPR,
      ].forEach(status => pushBlock(orderedBlocks, status))
    }
  } else {
    [
      ...WP_REVIEWER_HISTORY_STATUSES,
      WORK_PERMIT_STATUS.REJECTED,
      WORK_PERMIT_STATUS.APPR,
    ].forEach(status => pushBlock(orderedBlocks, status))
  }

  if (isEngineer) return orderedBlocks
  if (!canExternalViewerSeeHistorySection(permitStatus)) return []
  return orderedBlocks
    .filter(item => {
      if (item.status === WORK_PERMIT_STATUS.APPR) return !!item.by
      if (item.status === WORK_PERMIT_STATUS.REJECTED) return !!item.by
      return !!item.externalContent
    })
    .map((item, i) => {
      const next = { ...item, index: i + 1 }
      next.decision = getWorkPermitHistoryDisplayDecision({ item: next, isEngineer, emptyValue })
      return next
    })
}
